import { MAX_SIZE, MIN_SIZE, parseSq } from './geometry';
import { createGame, legalActions, royalAttacked } from './engine';
import type { Ruleset } from './types';

export interface Issue {
  level: 'error' | 'warning';
  message: string;
}

/** Find impossible or unplayable setups. Errors block play; warnings do not. */
export function validateRuleset(rs: Ruleset): Issue[] {
  const out: Issue[] = [];
  const err = (message: string) => out.push({ level: 'error', message });
  const warn = (message: string) => out.push({ level: 'warning', message });
  const { width: w, height: h } = rs.board;
  const r = rs.rules;

  if (!Number.isInteger(w) || !Number.isInteger(h) || w < MIN_SIZE || h < MIN_SIZE || w > MAX_SIZE || h > MAX_SIZE)
    err(`Board must be between ${MIN_SIZE}×${MIN_SIZE} and ${MAX_SIZE}×${MAX_SIZE}.`);

  const occupied = new Set<string>();
  const blocked = new Set([...rs.board.voids, ...rs.board.walls]);
  for (const p of rs.setup) {
    if (parseSq(p.sq, w, h) < 0) err(`Piece on ${p.sq} is outside the board.`);
    else if (blocked.has(p.sq)) err(`Piece on ${p.sq} stands on a removed or blocked square.`);
    if (occupied.has(p.sq)) err(`Two pieces share ${p.sq}.`);
    occupied.add(p.sq);
    if (!rs.pieces[p.type]) err(`Unknown piece type "${p.type}" on ${p.sq}.`);
  }

  for (const def of Object.values(rs.pieces)) {
    if (!def.moves.length && !def.abilities.some((a) => a.enabled))
      warn(`${def.name} has no moves and no abilities, it can never act.`);
    for (const m of def.moves) {
      if (!m.vectors.length) warn(`${def.name} has a movement rule with no directions.`);
      if (m.vectors.some(([x, y]) => x === 0 && y === 0)) err(`${def.name} has a zero-length move.`);
    }
    for (const a of def.abilities) if (a.enabled && a.range < 1) err(`${def.name}: ${a.kind} needs a range of at least 1.`);
  }

  const wc = r.winConditions;
  if (!wc.checkmate && !wc.captureAllRoyals && !wc.captureAnyRoyal && !wc.reachSquare)
    warn('No win condition is enabled: games can only end by annihilation, stalemate or draw.');
  if (wc.checkmate && !r.check) warn('Checkmate is a win condition but check is disabled, so checkmate cannot happen.');
  if (r.check && !wc.checkmate && !wc.reachSquare && (wc.captureAllRoyals || wc.captureAnyRoyal))
    warn('With the check rule on, kings are never captured: turn on checkmate or the game cannot be won.');
  if (wc.reachSquare && !r.reachSquares.w.length && !r.reachSquares.b.length)
    err('"Reach a square" is enabled but no target squares are set.');
  if (r.movesPerTurn < 1 || r.movesPerTurn > 5) err('Moves per turn must be between 1 and 5.');
  if (r.movesPerTurn > 1 && r.check) warn('With several moves per turn the first player can often win very quickly.');
  if (r.promotion.enabled) {
    const valid = r.promotion.choices.filter((c) => rs.pieces[c]);
    if (!valid.length && Object.values(rs.pieces).some((d) => d.promotes))
      warn('Promotion is enabled but there are no valid promotion choices.');
    if (r.promotion.zone < 1 || r.promotion.zone >= h) err('Promotion zone must cover between 1 and height-1 ranks.');
  }
  if (r.clock.initial < 0 || r.clock.increment < 0) err('Clock values cannot be negative.');

  for (const c of ['w', 'b'] as const) {
    const side = c === 'w' ? 'White' : 'Black';
    const mine = rs.setup.filter((p) => p.color === c);
    if (!mine.length) err(`${side} has no pieces.`);
    const royals = mine.filter((p) => rs.pieces[p.type]?.royal).length;
    if (!royals && (wc.checkmate || wc.captureAllRoyals || wc.captureAnyRoyal))
      warn(`${side} has no royal piece, so royal-based win conditions cannot be met against them.`);
    if (royals > 1 && r.check) warn(`${side} has ${royals} royal pieces; none of them may be left in check.`);
  }

  if (!out.some((i) => i.level === 'error')) {
    try {
      const s = createGame(rs);
      if (royalAttacked(s, 'b') && r.check) warn('Black starts in check.');
      if (!legalActions(s).length) err('White has no legal move in the starting position.');
    } catch (e) {
      err('The engine could not start this setup: ' + (e as Error).message);
    }
  }
  return out;
}
